/** Which cohorts the attacks land on (L1) — the deck's "under pressure" view.
 *
 *  Comparing magnitudes across named categories is a sorted horizontal bar
 *  (dashboard design rules §2), and one series means one colour for every bar
 *  (§3.1): the ranking is already carried by bar length and sort order, so
 *  tinting darker-where-bigger would spend the colour channel twice.
 *
 *  **What a "cohort" is here matters more than the chart.** TAP's SIEM feed
 *  carries no HR department, so the label is the recipient's mail domain and
 *  nothing else — NOVA does not join an org chart in to make it look like one.
 *  The caption and the note say so, because a bar labelled `norican.com` that a
 *  reader takes for "the Norican business unit" is a wrong answer delivered
 *  confidently (§5).
 *
 *  There is no k≥5 cohort floor: it was dropped on 2026-08-20 with the
 *  count-only VAP rule and replaced by the role gate on identities and the
 *  90-day retention window. Small cohorts therefore appear — but a cohort is a
 *  mail domain, and no name reaches this panel at any size.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { type DataGridColumnDef, EmptyState, Typography } from "@norican/react";

import { ChartCard, TABLE_TWIN_PARAM, type Verdict } from "@/components/board";
import type { DepartmentRow, SecuritySummary } from "@/lib/api";
import { formatCountCompact } from "@/lib/charts/config";
import { useDrawerParam } from "@/lib/drill";

import { EXEC_STALE_AFTER_MS, formatCount } from "./status";

/** `?data=targeted-cohorts` opens this chart's table twin. */
const TWIN_ID = "targeted-cohorts";

/** At or above this share of all attacks, one cohort is carrying the quarter. */
const CONCENTRATION_PCT = 50;

/** One series, one colour — never a ramp over nominal categories (§3.1). */
const COHORT_CONFIG: ChartConfig = {
  attack_count: { label: "Threat observations", color: "var(--norican-accent)" },
};

interface TargetedCohortsProps {
  /** Ranked cohorts, most attacked first — already truncated by the backend. */
  rows: DepartmentRow[];
  /** Headline figures; carries the distinct cohort count over the window. */
  summary: SecuritySummary;
  /** When the board payload was built, for the Freshness dot. */
  generatedAt: string | null | undefined;
}

export function TargetedCohorts({ rows, summary, generatedAt }: TargetedCohortsProps) {
  const drawer = useDrawerParam(TABLE_TWIN_PARAM);
  const ranked = [...rows].sort((a, b) => b.attack_count - a.attack_count);
  const total = ranked.reduce((sum, row) => sum + row.attack_count, 0);

  const twinColumns: DataGridColumnDef<DepartmentRow>[] = [
    { id: "department", header: "Cohort (mail domain)", accessor: (row) => row.department },
    {
      id: "attack_count",
      header: "Threat observations",
      accessor: (row) => row.attack_count,
      align: "end",
      cell: (row) => <span className="tabular-nums">{formatCount(row.attack_count)}</span>,
    },
    {
      id: "targeted_user_count",
      header: "Peak people in one cycle",
      accessor: (row) => row.targeted_user_count,
      align: "end",
      cell: (row) => <span className="tabular-nums">{formatCount(row.targeted_user_count)}</span>,
    },
  ];

  return (
    <ChartCard<DepartmentRow>
      title="Where the attacks land"
      caption={cohortCaption(ranked.length, summary)}
      since={generatedAt}
      staleAfterMs={EXEC_STALE_AFTER_MS}
      verdict={cohortVerdict(ranked, total)}
      tableTwin={{
        id: TWIN_ID,
        ariaLabel: "Threat observations per targeted cohort",
        title: "Targeted cohorts",
        data: ranked,
        getRowId: (row: DepartmentRow) => row.department,
        columns: twinColumns,
      }}
    >
      {ranked.length === 0 ? (
        <EmptyState
          size="sm"
          title="No cohort recorded an attack"
          description="Nothing has been attributed to a recipient domain in this window. On a live feed that points at collection rather than at a quiet quarter — check control health in section C."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Chart.Container
            config={COHORT_CONFIG}
            className="h-72"
            aria-label={`Threat observations for the ${ranked.length} most targeted cohorts over the last ${summary.window_days} days`}
          >
            <Chart.BarChart
              data={ranked}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis
                {...Chart.axisProps}
                type="number"
                allowDecimals={false}
                tickFormatter={formatCountCompact}
              />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="department" width={180} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    formatter={(value) => `${formatCount(Number(value))} threat observations`}
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="attack_count"
                fill="var(--color-attack_count)"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={() => drawer.open(TWIN_ID)}
              />
            </Chart.BarChart>
          </Chart.Container>

          <Typography variant="caption" tone="muted">
            A cohort is the <em>recipient&apos;s mail domain</em>, not an HR department: TAP&apos;s
            SIEM feed carries no organisational data and NOVA does not join any in. Observations sum
            across days. The people figure in the table is the largest number of distinct recipients
            seen in a single five-minute cycle, which under-counts a true daily distinct count on
            purpose — the alternative is keeping addresses across cycles, and bulk telemetry stays
            without identities. No cohort is suppressed for being small; what bounds identity here
            is the admin gate on names and the 90-day window, not a minimum group size.
          </Typography>
        </div>
      )}
    </ChartCard>
  );
}

/** Coverage beside the number (§5): how much of the window is on the chart. */
function cohortCaption(shown: number, summary: SecuritySummary): string {
  const distinct = summary.departments_targeted;
  if (shown === 0) {
    return `No cohort attacked in the last ${summary.window_days} days`;
  }
  if (distinct > shown) {
    return `Top ${shown} of ${formatCount(distinct)} cohorts attacked, last ${summary.window_days} days`;
  }
  return `${shown} cohort${shown === 1 ? "" : "s"} attacked, last ${summary.window_days} days`;
}

/** What the ranking means for where control effort goes. */
function cohortVerdict(ranked: DepartmentRow[], total: number): Verdict {
  const top = ranked[0];
  if (!top || total === 0) {
    return {
      label: "No data",
      tone: "neutral",
      text: "Nothing attributed to a cohort in this window, so this panel makes no claim about who is being aimed at.",
    };
  }

  const share = Math.round((top.attack_count / total) * 100);
  if (share >= CONCENTRATION_PCT) {
    return {
      label: "Concentrated",
      tone: "warning",
      text: `${top.department} absorbs ${share}% of all attack traffic. Aim training and control changes there first — spreading the same effort evenly would buy less.`,
    };
  }
  return {
    label: "Broad",
    tone: "neutral",
    text: `Pressure is spread across ${ranked.length} cohorts, with the worst at ${share}%. There is no single soft target to harden, so control changes have to be tenant-wide.`,
  };
}
